"use client";

import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  DesktopTableOfContents,
  MobileTableOfContents,
} from "./table-of-contents";
import { useTableOfContents } from "./useTableOfContents";

type TableOfContentsIslandProps = {
  articleId: string;
  desktopContainerId: string;
  slug: string;
  readableLabels?: boolean;
};

const DESKTOP_WIDTH = "clamp(13rem, 20vw, 19rem)";
const DESKTOP_MIN_WIDTH = "13rem";

export default function TableOfContentsIsland({
  articleId,
  desktopContainerId,
  slug,
  readableLabels = false,
}: TableOfContentsIslandProps) {
  const [desktopContainer, setDesktopContainer] = useState<HTMLElement | null>(
    null
  );
  const [isMounted, setIsMounted] = useState(false);

  const {
    headings,
    currentHeadingId,
    isTocCollapsed,
    isAutoCollapseEnabled,
    handleNavigate,
    handleToggleCollapse,
    handleAutoCollapseToggle,
    hasResolvedHeadings,
  } = useTableOfContents({ articleId, slug });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    const node = document.getElementById(desktopContainerId);
    setDesktopContainer(node);
  }, [desktopContainerId, isMounted]);

  useLayoutEffect(() => {
    if (!desktopContainer) return;
    // Keep the desktop placeholder in sync with the TOC state so the article
    // column expands when the TOC collapses.
    const width = isTocCollapsed ? "0px" : DESKTOP_WIDTH;
    const minWidth = isTocCollapsed ? "0px" : DESKTOP_MIN_WIDTH;
    desktopContainer.style.width = width;
    desktopContainer.style.minWidth = minWidth;
  }, [desktopContainer, isTocCollapsed]);

  const desktopPortal =
    isMounted && desktopContainer
      ? createPortal(
          <DesktopTableOfContents
            currentHeadingId={currentHeadingId}
            headings={headings}
            isAutoCollapse={isAutoCollapseEnabled}
            isCollapsed={isTocCollapsed}
            onAutoToggle={handleAutoCollapseToggle}
            onNavigate={handleNavigate}
            onToggleCollapse={handleToggleCollapse}
            readableLabels={readableLabels}
            isPending={!hasResolvedHeadings}
          />,
          desktopContainer
        )
      : null;

  return (
    <>
      <MobileTableOfContents
        currentHeadingId={currentHeadingId}
        headings={headings}
        onNavigate={handleNavigate}
        readableLabels={readableLabels}
        isPending={!hasResolvedHeadings}
      />
      {desktopPortal}
    </>
  );
}
